import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Save, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import ImageUploader from '../components/ImageUploader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const empty = {
  hero_image: '',
  heading: '',
  intro: '',
  mission: '',
  vision: '',
  highlights: [],
  story_image: '',
};

const AdminAbout = () => {
  const { adminAuth } = useOutletContext();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchAboutContent = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/about`);
      if (res.ok) {
        const data = await res.json();
        setForm({ ...empty, ...data });
      }
    } catch (e) {
      toast.error('Failed to load About content');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAboutContent();
  }, [fetchAboutContent]);

  const setField = (key, val) => setForm((prev) => ({ ...prev, [key]: val }));

  const updateHighlight = (idx, val) => {
    const next = [...form.highlights];
    next[idx] = val;
    setField('highlights', next);
  };

  const addHighlight = () => setField('highlights', [...form.highlights, '']);
  const removeHighlight = (idx) => setField('highlights', form.highlights.filter((_, i) => i !== idx));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form, highlights: form.highlights.filter((h) => h.trim()) };
      const res = await fetch(`${API_URL}/api/admin/about`, {
        method: 'PUT',
        headers: {
          Authorization: `Basic ${adminAuth}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success('About page updated');
      } else {
        toast.error('Failed to save');
      }
    } catch (e) {
      toast.error('Connection error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">About Us Page</h1>
          <p className="text-gray-600">Edit the content shown on the public About page</p>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="save-about-btn" className="bg-amber-600 hover:bg-amber-700">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <Card>
        <CardHeader><CardTitle>Hero Section</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <ImageUploader
            value={form.hero_image}
            onChange={(url) => setField('hero_image', url)}
            uploadEndpoint="/api/admin/upload/about"
            adminAuth={adminAuth}
            label="Hero Background Image"
          />
          <div>
            <label className="block text-sm font-medium mb-1">Heading</label>
            <Input data-testid="about-heading-input" value={form.heading} onChange={(e) => setField('heading', e.target.value)} placeholder="About The Navkar Travels" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Our Story</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Intro / Story Text</label>
            <Textarea data-testid="about-intro-input" rows={5} value={form.intro} onChange={(e) => setField('intro', e.target.value)} />
          </div>
          <ImageUploader
            value={form.story_image}
            onChange={(url) => setField('story_image', url)}
            uploadEndpoint="/api/admin/upload/about"
            adminAuth={adminAuth}
            label="Story Image"
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Mission & Vision</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Mission</label>
            <Textarea data-testid="about-mission-input" rows={3} value={form.mission} onChange={(e) => setField('mission', e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Vision</label>
            <Textarea data-testid="about-vision-input" rows={3} value={form.vision} onChange={(e) => setField('vision', e.target.value)} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Why Choose Us (Highlights)</CardTitle>
            <Button type="button" variant="outline" size="sm" onClick={addHighlight} data-testid="add-highlight-btn">
              <Plus className="w-4 h-4 mr-1" /> Add
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {form.highlights.length === 0 && <p className="text-sm text-gray-500">No highlights yet. Click "Add" to create one.</p>}
          {form.highlights.map((h, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <Input
                data-testid={`highlight-input-${idx}`}
                value={h}
                onChange={(e) => updateHighlight(idx, e.target.value)}
                placeholder="e.g. Fully customized itineraries"
              />
              <button type="button" onClick={() => removeHighlight(idx)} className="p-2 text-red-500 hover:bg-red-50 rounded" data-testid={`remove-highlight-${idx}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} className="bg-amber-600 hover:bg-amber-700">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
};

export default AdminAbout;
